

import wx from './weixin-js-sdk-ts/index';
import { PayExportOptions } from './types/WebPayTypes';
import { getPreOrder } from './services/webPay';
import { checkPayParam, isWeixinClient, isIOS } from '../tools/utils';
import { getWechatUserInfoService } from './services/wechatAccountService';

/**
 * @description 微信支付宝支付相关代码【是否安装微信支付宝在端内可以自己判断】
 * @param {Function} aliPayH5InApp 佣金宝|弹指间App端内走支付宝支付
 * @param {Function} aliPayH5 端外H5走支付宝支付
 * 
 * @param {Function} wechatH5PayInApp 佣金宝|弹指间App端内微信支付
 * @param {Function} wechatH5Pay 端外H5走微信支付
 * @param {Function} jsApiPay 微信端内走jsApi支付
 * @param {Function} h5Pay 支付统一入口支付函数
 */

// 支付api权限
const WEIXIN_JS_API_LIST: any = ['chooseWXPay']

/**
 * 支付宝支付，在H5内使用
 * 安卓端无论使用 urlscheme 方式还是直接在app内套用H5的链接跳转都不用考虑 return_url 问题
 * iOS 需传递 return_url, 如果不传 则 默认是 urlscheme 方式跳转
 * @param data: 预支付订单接口返回的结果
 * @param options: 用户传过来的参数
 */
const aliPayH5InApp = async (data: any, options: PayExportOptions) => {
  const payKey = isIOS ? 'iosAppPayUrl' : 'androidAppPayUrl'
  try {
    // 传递了return_url 并且return_url 中没有scheme 会在App中使用H5的支付方式， 否则使用scheme方式进行支付
    if (options.return_url) {
      if (options.return_url.indexOf('scheme') > -1 && !isIOS) {
        // 安卓端只要有scheme就直接跳scheme
        location.href = data.result[`${payKey}`];
      } else {
        const form = data.result.payForm
        let aliPayDiv = document.querySelector('#alipauH5')
        if (!aliPayDiv) {
          aliPayDiv = document.createElement('div')
          aliPayDiv.id = 'alipauH5'
          document.body.appendChild(aliPayDiv)
        }
        aliPayDiv.innerHTML = form
        document.forms[0].submit()
      }
    } else {
      location.href = data.result[`${payKey}`];
    }
  } catch (error) {
    return {
      url: data.result[`${payKey}`],
      code: 500
    }
  }
}

/**
 * 支付宝H5支付
 * @param options
 */
const aliPayH5 = async (options: PayExportOptions) => {
  const comOptions: any = {
    product_code: 'QUICK_MSECURITY_PAY',
    trade_type: 'H5',
    ...options
  }
  try {
    const data = await getPreOrder(comOptions)
    // console.log(data)
    if (data.code === 0) {
      if (options.source === 'app') {
        return await aliPayH5InApp(data, options)
      } else {
        const form = data.result
        let aliPayDiv = document.querySelector('#alipauH5')
        if (!aliPayDiv) {
          aliPayDiv = document.createElement('div')
          aliPayDiv.id = 'alipauH5'
          document.body.appendChild(aliPayDiv)
        }
        aliPayDiv.innerHTML = form
        document.forms[0].submit()
      }
    } else {
      return data
    }
  } catch (error) {
    return {
      message: '下单失败',
      code: 500
    }
  }
}

/**
 * 调用微信jsapi支付
 * @param data 支付参数
 * @returns
 */
const wechatChoosePay = (data: any) => {
  const { timeStamp, nonceStr, sign, package: pe } = data

  return new Promise(resolve => {
    wx.ready(() => {
      wx.chooseWXPay({
        nonceStr,
        package: pe,
        signType: 'MD5',
        paySign: sign,
        timestamp: timeStamp,
        success: ({errMsg}) => {
          resolve({code: 0, result: '支付成功', message: errMsg});
        },
        fail: ({errMsg}) => {
          resolve({code: -99, result: '支付失败', message: errMsg});
        },
        cancel: ({errMsg}) => {
          resolve({code: -100, result: '支付取消', message: errMsg});
        },
        // complete: ({errMsg}) => {
        //   resolve({code: 0, result: '支付完成', message: errMsg});
        // }
      })
    })
    wx.error(res => {
      resolve({code: -999, result: '未知错误', message: res});
    })
  })
}

/**
 * 微信jsapi支付
 * @param options 统一下单参数
 */
const jsApiPay = async (options: PayExportOptions) => {
  let payOptions: any = { trade_type: 'JSAPI', ...options }

  try {
    const { code, result } = await getPreOrder(payOptions)
    const { appId, timeStamp, nonceStr, sign } = result

    if (code === 0 && appId) {
      const wechatConfig = {
        appId,
        nonceStr,
        debug: false,
        signature: sign,
        openTagList: [],
        timestamp: timeStamp,
        jsApiList: WEIXIN_JS_API_LIST
      }
      wx.config(wechatConfig)

      return await wechatChoosePay(result)
    } else {
      return { code, result }
    }
  } catch (error) {
    return { message: '下单失败', code: 500 }
  }
}

/**
 * 在App内使用微信H5支付
 *
 * 若传递了 return_url 则会使用H5支付流程
 *
 * 若不传 return_url 则使用 scheme 方式跳转支付
 * @description 佣金宝APP内使用微信支付
 * @param data 预支付获取订单的结果
 * @param options 用户进行支付传递的参数
 */
const wechatH5PayInApp = async (data: any, options: PayExportOptions) => {
  // 如果app的return_url中没有scheme的参数则使用H5的支付方式，否则使用scheme支付方式

  const rurl = options.return_url
  const { appPayUrl, mwebUrl } = data

  if (rurl) {
    if (rurl.indexOf('scheme') > -1 && !isIOS) {
      // 安卓直接scheme跳转
      location.href = appPayUrl;
    } else {
      window.location.replace(`${mwebUrl}&redirect_url=${encodeURIComponent(rurl)}`);
    }
  } else {
    location.href = appPayUrl;
  }
}

/**
 * @description 微信H5支付-不走jsapi【端内、端外】
 * @param options H5支付参数
 * @returns
 */
const wechatH5Pay = async (options: PayExportOptions) => {
  let payOptions: any = { trade_type: 'MWEB', ...options }

  try {
    const { code, result }: any = await getPreOrder(payOptions)
    const { mwebUrl } = result || {}

    if (code == 0 && mwebUrl) {
      // location.href = `${mwebUrl}&redirect_url=${encodeURIComponent(redirect_url)}`
      // 修改成replace方式跳转兼容三星和chrome浏览器无法返回回调页面问题

      // 佣金宝走的逻辑
      if (options.source === 'app') {
        return await wechatH5PayInApp(result, options);
      } else {
        const redirect_url = options.return_url || location.href
        window.location.replace(`${mwebUrl}&redirect_url=${encodeURIComponent(redirect_url)}`);
      }
    } else {
      return { code, result };
    }
  } catch (error) {
    return {message: '下单失败',
      code: 500
    }
  }
}

/**
 * @description WEB-H5支付支付方法【微信，支付宝】
 */
const h5Pay = async (options: PayExportOptions) => {
  const { source, app_name, account_name, wxWebAuthCode, ...last } = options || {}

  // 请求参数拼接
  const params = {
    source: source || 'h5',
    app_name: app_name || 'yjb',
    account_name: account_name || 'gjzq',
    ...last
  }

  // 检验参数
  const checkResult: any = await checkPayParam(params)

  // 如果参数异常则抛出异常
  if (!checkResult || (checkResult && checkResult.code)) {
    return checkResult
  }

  // 微信支付
  const isWxPay = params.pay_type == 1

  if (isWxPay) {
    // 微信端内走jsapi方式
    if (isWeixinClient()) {
      // 获取openId
      const { result } = await getWechatUserInfoService({
        app_name,
        account_name,
        code: wxWebAuthCode
      })

      params.openid = result.openid

      return await jsApiPay(params)
    } else {
      return await wechatH5Pay(params)
    }
  } else {
    return await aliPayH5(params)
  }
}

export default { h5Pay }
